/**
 * Hook for streaming RAG queries with real-time updates
 * 
 * Features:
 * - Real-time response streaming
 * - Status updates (searching, thinking, generating)
 * - Partial response display
 * - Citation metadata
 * - Abort support
 */

import { useState, useCallback, useRef } from 'react';
import {
  queryRAGStream,
  RAGStreamStatusData,
  RAGStreamChunkData,
  RAGStreamCitationData,
  RAGStreamCompleteData,
  RAGStreamErrorData,
} from '../api/rag';
import { RAGQueryRequest } from '../types/api';

export type StreamingStage = 
  | 'idle' 
  | 'started' 
  | 'context' 
  | 'retrieval' 
  | 'retrieval_complete' 
  | 'generating' 
  | 'thinking' 
  | 'complete' 
  | 'error'
  | 'no_evidence';

export interface StreamingCitation {
  index: number;
  source: string;
  page?: number;
  section?: string;
  content?: string;
  confidence: number;
}

export interface StreamingState {
  stage: StreamingStage;
  message: string;
  partialAnswer: string;
  citations: StreamingCitation[];
  finalAnswer: string | null;
  error: string | null;
  isStreaming: boolean;
  elapsed: number;
  thinkingTime: number;
  reasoningTokens: number;
  outputTokens: number;
  retrievalTime: number;
  generationTime: number;
  totalTime: number;
  evidenceCount: number;
}

const initialState: StreamingState = {
  stage: 'idle',
  message: '',
  partialAnswer: '',
  citations: [],
  finalAnswer: null,
  error: null,
  isStreaming: false,
  elapsed: 0,
  thinkingTime: 0,
  reasoningTokens: 0,
  outputTokens: 0,
  retrievalTime: 0,
  generationTime: 0,
  totalTime: 0,
  evidenceCount: 0,
};

export function useStreamingRAG() {
  const [state, setState] = useState<StreamingState>(initialState);
  const abortControllerRef = useRef<AbortController | null>(null);
  
  const startQuery = useCallback((
    projectId: string,
    request: Partial<RAGQueryRequest>
  ) => {
    // Cancel any existing stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Reset state
    setState({
      ...initialState,
      isStreaming: true,
      stage: 'started',
      message: 'Starting query...',
    });
    
    // Start streaming
    abortControllerRef.current = queryRAGStream(projectId, request, {
      onStatus: (data: RAGStreamStatusData) => {
        setState(prev => ({
          ...prev,
          stage: data.stage as StreamingStage,
          message: data.message,
          elapsed: data.elapsed || prev.elapsed,
          thinkingTime: data.thinking_time || prev.thinkingTime,
          reasoningTokens: data.reasoning_tokens || prev.reasoningTokens,
          outputTokens: data.output_tokens || prev.outputTokens,
          retrievalTime: data.retrieval_time || prev.retrievalTime,
          evidenceCount: data.evidence_count || prev.evidenceCount,
        }));
      },
      
      onChunk: (data: RAGStreamChunkData) => {
        setState(prev => ({
          ...prev,
          stage: 'generating',
          partialAnswer: prev.partialAnswer + data.content,
        }));
      },
      
      onCitation: (data: RAGStreamCitationData) => {
        setState(prev => ({
          ...prev,
          citations: [
            ...prev.citations,
            {
              index: data.index,
              source: data.source,
              page: data.page,
              section: data.section,
              confidence: data.confidence,
            },
          ],
        }));
      },
      
      onComplete: (data: RAGStreamCompleteData) => {
        setState(prev => ({
          ...prev,
          stage: 'complete',
          message: 'Response complete',
          finalAnswer: data.answer,
          citations: data.citations.map(c => ({
            index: c.index,
            source: c.source,
            page: c.page,
            section: c.section,
            content: c.content,
            confidence: c.confidence,
          })),
          isStreaming: false,
          retrievalTime: data.retrieval_time || prev.retrievalTime,
          generationTime: data.generation_time || 0,
          totalTime: data.total_time || 0,
          evidenceCount: data.evidence_count || prev.evidenceCount,
        }));
        abortControllerRef.current = null;
      },
      
      onError: (data: RAGStreamErrorData) => {
        setState(prev => ({
          ...prev,
          stage: 'error',
          error: data.message,
          message: `Error: ${data.message}`,
          isStreaming: false,
        }));
        abortControllerRef.current = null;
      },
    });
  }, []);
  
  const cancelQuery = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setState(prev => ({
        ...prev,
        isStreaming: false,
        stage: 'idle',
        message: 'Query cancelled',
      }));
    }
  }, []);
  
  const reset = useCallback(() => {
    cancelQuery();
    setState(initialState);
  }, [cancelQuery]);
  
  return {
    ...state,
    startQuery,
    cancelQuery,
    reset,
  };
}

export default useStreamingRAG;

